import { Form } from "antd";
import { useEffect } from "react";
import { FormModal } from "@/shared/components/FormModal";
import { useCreateEquipment, useUpdateEquipment } from "../hooks/useEquipment";
import { useEquipmentStore } from "../store/equipment.store";
import type { CreateEquipmentRequest } from "../types";
import { EquipmentForm } from "./EquipmentForm";

interface EquipmentFormModalProps {
	organizationId: string;
}

export function EquipmentFormModal({ organizationId }: EquipmentFormModalProps) {
	const [form] = Form.useForm<CreateEquipmentRequest & { licensePlate?: string }>();
	const { modalOpen, editingItem, defaultOrganizationId, closeModal } = useEquipmentStore();

	const orgId = editingItem?.organizationId ?? defaultOrganizationId ?? organizationId;

	const { mutate: create, isPending: isCreating } = useCreateEquipment();
	const { mutate: update, isPending: isUpdating } = useUpdateEquipment();

	const close = () => {
		form.resetFields();
		closeModal();
	};

	useEffect(() => {
		if (modalOpen) {
			if (editingItem) {
				form.setFieldsValue({
					name: editingItem.name,
					typeId: editingItem.typeId ?? editingItem.type?.id,
					serialNumber: editingItem.serialNumber,
					licensePlate: editingItem.licensePlate ?? undefined,
					organizationId: editingItem.organizationId,
					status: editingItem.status,
					gpsDeviceId: editingItem.gpsDeviceId ?? editingItem.gpsDevice?.id ?? undefined,
					brigadeId: editingItem.brigadeId ?? editingItem.brigade?.id ?? undefined,
				});
			} else {
				form.resetFields();
				form.setFieldsValue({
					organizationId: orgId ?? undefined,
					status: "ACTIVE",
				});
			}
		}
	}, [modalOpen, editingItem, orgId, form]);

	const onFinish = (values: CreateEquipmentRequest & { licensePlate?: string }) => {
		const payload: CreateEquipmentRequest = {
			name: values.name,
			organizationId: values.organizationId ?? organizationId,
			typeId: values.typeId,
			serialNumber: values.serialNumber,
			licensePlate: values.licensePlate,
			gpsDeviceId: values.gpsDeviceId,
			brigadeId: values.brigadeId ?? null,
			status: values.status ?? "ACTIVE",
		};

		if (editingItem) {
			update(
				{
					id: editingItem.id,
					data: {
						name: payload.name,
						typeId: payload.typeId,
						serialNumber: payload.serialNumber,
						licensePlate: payload.licensePlate,
						gpsDeviceId: payload.gpsDeviceId,
						brigadeId: payload.brigadeId,
						status: payload.status,
					},
				},
				{ onSuccess: close }
			);
		} else {
			create(payload, { onSuccess: close });
		}
	};

	return (
		<FormModal
			open={modalOpen}
			editing={!!editingItem}
			title="Yangi texnika"
			editTitle="Texnikani tahrirlash"
			onCancel={close}
			onOk={form.submit}
			confirmLoading={isCreating || isUpdating}
			width={720}
		>
			<Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
				<EquipmentForm organizationId={orgId ?? undefined} />
			</Form>
		</FormModal>
	);
}
